import axios from 'axios';
import Cookies from 'js-cookie';

import { history } from '../store';

export const saveTokens = async (tokens) => {
  const accessExpiresDate = new Date(tokens.access.expiration);
  const refreshExpiresDate = new Date(tokens.refresh.expiration);

  Cookies.set('accessToken', tokens.access.token, { expires: accessExpiresDate });
  Cookies.set('refreshToken', tokens.refresh.token, { expires: refreshExpiresDate });
};

export const logout = async () => {
  Cookies.remove('accessToken');
  Cookies.remove('refreshToken');
  delete callApi.defaults.headers.common.Authorization;
  history.push('/login');
};

let isRefreshing = false;
let refreshPromise = null;
let pendingRequests = [];

const refreshClient = axios.create({ headers: { 'Content-Type': 'application/json' } });

const subscribeTokenRefresh = (cb) => pendingRequests.push(cb);
const onRefreshSuccess = (newAccessToken) => {
  isRefreshing = false;
  refreshPromise = null;

  pendingRequests.forEach((cb) => cb(newAccessToken));
  pendingRequests = [];
};
const onRefreshFailed = (err) => {
  isRefreshing = false;
  refreshPromise = null;

  pendingRequests.forEach((cb) => cb(null, err));
  pendingRequests = [];
};

async function refreshTokens() {
  const refreshToken = Cookies.get('refreshToken');
  if (!refreshToken) throw new Error('No refresh token');

  const { data } = await refreshClient.post('/api/auth/refresh', { refreshToken });
  await saveTokens(data);

  callApi.defaults.headers.common.Authorization = `Bearer ${data.access.token}`;
  return data.access.token;
}

const callApi = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

callApi.interceptors.request.use(
  (config) => {
    const accessToken = Cookies.get('accessToken');

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    } else {
      delete config.headers.Authorization;
      delete callApi.defaults.headers.common.Authorization;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

callApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) return Promise.reject(error);

    const status = error.response.status;
    const shouldTryRefresh = (status === 401 || status === 403) && !originalRequest._retry;

    if (!shouldTryRefresh) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken, refreshErr) => {
          if (refreshErr || !newToken) {
            reject(refreshErr || new Error('Refresh failed'));
            return;
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(callApi(originalRequest));
        });
      });
    }

    isRefreshing = true;
    refreshPromise = refreshTokens();

    try {
      const newAccessToken = await refreshPromise;

      onRefreshSuccess(newAccessToken);

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return callApi(originalRequest);
    } catch (refreshErr) {
      onRefreshFailed(refreshErr);

      await logout();
      return Promise.reject(refreshErr);
    }
  },
);

export default callApi;
